import {AfterViewInit, Component, Input, OnDestroy, OnInit, Output, EventEmitter} from '@angular/core';
import Article from '../../../smoothr-web-app-core/models/Article';
import {PreorderType} from '../../../smoothr-web-app-core/enums/PreorderType';
import {OrderType} from '../../../smoothr-web-app-core/enums/OrderType';
import ArticleGroup from '../../../smoothr-web-app-core/models/ArticleGroup';
import {OrderUtils} from '../../../smoothr-web-app-core/utils/order-utils';
import {environment} from 'src/environments/environment';
import {defaultsToArticleOption, getPrice, numberD, numberToCurrency} from '../../../smoothr-web-app-core/utils/utils';
import {TranslateService} from '@ngx-translate/core';
import Order from 'src/smoothr-web-app-core/models/Order';
import {RepositoryService} from 'src/smoothr-web-app-core/services/repository/repository.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import SubscriptionArticles from 'src/smoothr-web-app-core/models/Subscription';
import {emit} from 'process';
import {AppComponent} from 'src/app/app.component';
import Localization from 'src/smoothr-web-app-core/models/Localization';

@Component({
	selector: 'app-item-view',
	templateUrl: './item-view.component.html',
	styleUrls: ['item-view.component.scss']
})
export class ItemViewComponent implements OnInit, OnDestroy, AfterViewInit {
	private onDestroy$ = new Subject();
	public isLargeScreen = AppComponent.largeScreen;
	public isTabletScreen = AppComponent.tabletScreen;
	private _currency: string;

	get currency(): string {
		return this._currency;
	}

	@Input()
	set currency(value: string) {
		this._currency = value;
		this.reloadMeta();
	}
	@Input() order: Order;
	@Input() customStyle = false;
	// @Input() isArticlesInSubscription: boolean = false;
	price: number;
	ot = OrderType;
	environment = environment;
	@Input() hasOrder = false;
	@Input() subscriptionArticles: ArticleGroup[] = [];
	@Input() subscriptionsUser: SubscriptionArticles[] = [];
	@Input() isFavourite: boolean = false;
	@Input() categoryName: Localization;
	isOrderAlreadyInBasket = 0;
	priceText = '';
	isArticlesInSubsc = false;
	@Output() selectArticle = new EventEmitter<void>();
	@Output() selectArticleByPlus = new EventEmitter<void>();
	@Output() increase = new EventEmitter<void>();
	@Output() minus = new EventEmitter<void>();
	@Output() addFavourite = new EventEmitter<void>();

	numberToCurrency = numberToCurrency;

	// the meta line under the article name: dietary tags | price per kg | calories
	articleTags = '';
	measurement = '';
	calories = '';

	// tag identifiers the filter menu offers - anything else is an internal flag
	// (hide_cart, cutlery, next_day, ...) and must not show up in the article list
	private static readonly DIET_TAG_IDENTIFIERS = [
		'vegetarian',
		'vegan',
		'new',
		'glutenFree',
		'noCarb',
		'lactoseFree',
		'basic',
		'notProtein'
	];

	constructor(
		private repository: RepositoryService,
		private translate: TranslateService
	) {}
	ngOnInit(): void {
		this.checkHowManyQuantityInBasket(this.repository._order);
		this.repository.order.pipe(takeUntil(this.onDestroy$)).subscribe(order => {
			this.checkHowManyQuantityInBasket(order);
		});

		this.repository.customerAuth.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
			this.reloadPrice();
		});

		this.translate.onLangChange.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
			this.reloadMeta();
		});
	}
	ngOnDestroy(): void {
		this.onDestroy$.next();
	}
	ngAfterViewInit(): void {
		//Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
		//Add 'implements AfterViewInit' to the class.
	}
	private _article: Article;

	get article(): Article {
		return this._article;
	}

	@Input()
	set article(value: Article) {
		this._article = value;
		this.reloadPrice();
	}

	private _preorderType: PreorderType = null;

	get preorderType(): PreorderType {
		return this._preorderType;
	}

	@Input()
	set preorderType(value: PreorderType) {
		this._preorderType = value;
		this.reloadPrice();
	}

	private reloadPrice() {
		if (this.article && this.preorderType) {
			const articleGroup = new ArticleGroup();
			articleGroup.article = this.article;
			articleGroup.groups = defaultsToArticleOption(
				this.article,
				[],
				this.article.defaults,
				OrderType.PREORDER,
				this.preorderType
			);
			articleGroup.quantity = 1;
			const price = OrderUtils.articleGroupsTotalPrice([articleGroup], OrderType.PREORDER, this.preorderType);
			if (
				this.repository
					.mapUserSubscriptions(this.repository?._customerAuth?.subscription)
					.find(it => it.articles.includes(this.article?._id))
			) {
				// this.price = this.isArticlesInSubscription(this.repository._subscriptionArticles, price);
				this.price = price;
			} else {
				this.price = price;
			}
		} else {
			this.price = 0;
		}
		this.priceText = `${this.price}`;
		this.reloadMeta();
	}
	private reloadMeta() {
		this.articleTags = this.getArticleTags();
		this.measurement = this.showMeasurment();
		this.calories = this.showCalories();
	}
	public showTags() {
		return this.article.tags?.length > 0 ? this.article.tags?.map(tag => tag.name.de).join(', ') : '';
	}
	getArticleTags(): string {
		return (this.article?.tags ?? [])
			.filter(it => ItemViewComponent.DIET_TAG_IDENTIFIERS.includes(it?.identifier))
			.map(it => this.localized(it.name) || it.identifier)
			.filter(it => !!it)
			.join(', ');
	}
	public showWeight() {
		return this.article?.nutritions?.portionInG
			? '(' + Math.round(numberD(this.article?.nutritions?.portionInG)).toString() + 'g' + ')'
			: '';
	}
	public showMeasurment(): string {
		const portionInG = numberD(this.article?.nutritions?.portionInG);
		if (!portionInG || !this.price) {
			return '';
		}

		return numberToCurrency((1000 * this.price) / portionInG, this.currency) + '/kg';
	}
	public showCalories(): string {
		const kcal = numberD(this.article?.nutritions?.energyKcalPerPortion);
		if (!kcal || isNaN(kcal)) {
			return '';
		}

		return (
			Math.round(kcal).toLocaleString(navigator.language) + ' ' + this.translate.instant('item_view.leafs')
		);
	}
	private localized(value: Localization): string {
		if (!value) {
			return '';
		}

		return value[this.translate.currentLang] || value.de || '';
	}
	checkHowManyQuantityInBasket(order: Order) {
		const foundArticleInOrder = order.orderedSubscriptionArticles
			.concat(order?.orderedArticles)
			.filter(it => it.quantity > 0 && it.article._id === this.article._id);

		this.isOrderAlreadyInBasket = foundArticleInOrder ? foundArticleInOrder.reduce((a, b) => a + b.quantity, 0) : 0;
	}
	isInOrder() {
		if (this.order)
			return (
				(this.order &&
					this.order.orderedArticles.length > 0 &&
					this.order.orderedArticles.find(it => this.article._id === it.article._id)) ||
				(this.order?.orderedSubscriptionArticles?.length > 0 &&
					this.order?.orderedSubscriptionArticles?.find(it => this.article._id === it?.article?._id))
			);
		else false;
	}
	isArticlesInSubscription(ids: SubscriptionArticles[], price: number) {
		const subscriptionWithArticle = ids.find(it => it.articles.includes(this.article?._id));

		if (subscriptionWithArticle) {
			if (this.subscriptionArticles.length === 0) {
				if (subscriptionWithArticle.maxPricePerArticle > price) {
					return 0;
				} else {
					return price;
				}
			} else {
				const pricesOfSubscriptionArticle = this.subscriptionArticles.map(it =>
					OrderUtils.totalPrice(it, this.order.type, this.order.preorder.type)
				);
				if (pricesOfSubscriptionArticle.reduce((a, b) => a + b, 0) > subscriptionWithArticle.maxPricePerOrder) {
					return price;
				} else {
					return 0;
				}
			}
		}
	}
	isArticleInSubscription(article: Article): boolean {
		return !!(this.subscriptionsUser ?? []).find(it => it?.articles?.includes(article?._id));
	}
	showSubscription(article: Article) {
		return (this.subscriptionsUser ?? []).find(it => it?.articles?.includes(article?._id));
	}
	addArticle() {
		this.selectArticle.emit();
	}
	addArticleByPlus(e: Event) {
		console.log('addArticleByPlus');
		this.selectArticleByPlus.emit();

		e.stopPropagation();
		e.preventDefault();
	}
	remove(e: any) {
		this.minus.emit();
		e.stopPropagation();
		e.preventDefault();
	}

	add(e: any) {
		this.increase.emit();
		e.stopPropagation();
		e.preventDefault();
	}
}
